import { buildCssClassName, getTransitionTwClasses } from "@/3514/utils"

const sharedModalStyles = {
    body: (): string =>
        buildCssClassName(
            "flex",
            "flex-col",
            "max-w-[95%]",
            "md:max-w-[620px]",
            "w-full",
            "max-h-[90%]",
            "md:min-h-fit",
            "py-[35px]",
            "px-[30px]",
            "bg-white",
            "rounded-[10px]",
            "relative",
            "h-fit"
        ),
    header: (): string => "pb-[8px]",
    container: (): string => "flex flex-col gap-y-[40px] items-center",
    middleWrapper: (): string => "flex flex-col gap-y-[8px] items-center w-full",
    buttonWrapper: (): string => "flex flex-col gap-y-[10px] items-center"
}

export default {
    entity: {
        section: (): string =>
            buildCssClassName(
                "[display:grid]",
                "gap-[15px]",
                "md:gap-[50px]",
                "w-full",
                "[container:content]",
                "[content-visibility:visible]"
            ),
        listLive: (): string =>
            buildCssClassName(
                "[display:grid]",
                "gap-[15px]",
                "mb-[20px]",
                "max-md:my-[20px]",
                "[container:content]",
                "[content-visibility:visible]"
            ),
        listGeneral: (): string => "[display:grid] gap-[15px] [container:content] [content-visibility:visible]",
        emptyMessageContainer: (): string =>
            buildCssClassName(
                "flex",
                "gap-x-[5px]",
                "items-center",
                "justify-start",
                "w-full",
                "py-[10px]",
                "px-[15px]",
                "bg-gray-500",
                "rounded-[5px]"
            )
    },
    modalSessionInfo: {
        container: ({ isCancelled }: { isCancelled: boolean }): string =>
            buildCssClassName(
                "bg-blue-600",
                "rounded-[10px]",
                "py-[10px]",
                "px-[15px]",
                "flex",
                "items-center",
                "gap-x-[10px]",
                "md:min-w-[280px]",
                "w-fit",
                { "opacity-[60] max-w-full": isCancelled },
                { "max-w-[290px]": !isCancelled },
                getTransitionTwClasses("all", 150)
            ),
        cancelledHint: (): string => "flex items-center gap-x-[5px] ml-auto mr-0"
    },
    modal: {
        cancelled: {
            ...sharedModalStyles,
            cancellation: (): string => "md:max-w-[280px]",
            infoWrapper: (): string => "flex flex-col self-center items-center gap-y-[10px] py-[20px] w-full"
        },
        gift: {
            ...sharedModalStyles,
            checkboxContainer: (): string => "flex items-center gap-x-[3px] py-[5px]",
            infoWrapper: (): string => "flex flex-col items-center gap-y-[inherit]",
            description: (): string =>
                "font-[300] text-gray-100 text-[16px] leading-[24px] text-center text-pretty hyphens-auto",
            descriptionStrong: (): string => "font-[800]"
        },
        gifted: { ...sharedModalStyles },
        lateEdit: { ...sharedModalStyles },
        reschedules: {
            ...sharedModalStyles,
            list: (): string =>
                buildCssClassName(
                    "my-[20px]",
                    "flex",
                    "flex-col",
                    "gap-y-[5px]",
                    "self-center",
                    "w-full",
                    "md:max-w-[400px]",
                    "relative",
                    "max-h-[160px]",
                    "no-scrollbar",
                    "overflow-y-auto",
                    "overscroll-contain",
                    "touch-auto"
                ),
            item: (): string => "flex py-[8px] px-[10px] flex-col gap-y-[5px] rounded-[10px] bg-blue-600"
        },
        cancel: {
            ...sharedModalStyles,
            infoContainer: (): string => "py-[20px] mx-auto",
            statusContainer: (): string => "flex flex-col gap-y-[8px]",
            statusMessage: (): string => "flex items-center justify-center gap-x-[10px] pt-[10px]",
            statusText: (): string => "leading-[21px] text-[14px] text-gray-200 font-[300] text-pretty text-center"
        },
        scheduleAvailable: {
            container: (): string =>
                buildCssClassName(
                    "px-[15px]",
                    "md:px-[30px]",
                    "py-[35px]",
                    "bg-white",
                    "rounded-[10px]",
                    "max-h-[90%]",
                    "md:max-h-[550px]",
                    "md:max-w-[620px]",
                    "flex",
                    "flex-col",
                    "justify-center",
                    "max-w-[90%]",
                    "w-full",
                    "relative",
                    "md:h-fit"
                ),
            body: {
                container: (): string => "flex flex-col gap-y-[8px] pt-[8px]",
                button: (): string => "!mt-[40px]",
                session: {
                    holder: (): string => "flex flex-col gap-y-[20px]",
                    container: ({ withPaddings }: { withPaddings?: boolean } = { withPaddings: false }): string =>
                        buildCssClassName("flex flex-col gap-y-[15px]", withPaddings && "pt-[10px]"),
                    wrapper: (): string =>
                        buildCssClassName(
                            "px-[10px]",
                            "md:px-[20px]",
                            "py-[10px]",
                            "rounded-[10px]",
                            "flex",
                            "self-center",
                            "items-center",
                            "justify-between",
                            "gap-x-[20px]",
                            "md:gap-x-[30px]",
                            "w-[calc(100%-4px)]",
                            "translate-x-[2px]",
                            "cursor-pointer",
                            "border-[0.5px]",
                            "border-blue-300",
                            "outline",
                            "outline-[0.5px]",
                            "outline-transparent",
                            "hover:border-blue-200",
                            "hover:outline-blue-200",
                            "focus:visible:border-blue-200",
                            "focus:visible:outline-blue-200",
                            getTransitionTwClasses("colors,[outline-color]", 200)
                        ),
                    nameWrapper: (): string => "w-full",
                    name: (): string => "max-w-[130px] truncate md:max-w-fit",
                    expiration: (): string => "mb-auto min-w-fit"
                }
            },
            opener: {
                container: (): string =>
                    buildCssClassName(
                        "w-full",
                        "border-[1px]",
                        "border-red-200",
                        "bg-white",
                        "hover:bg-red-500",
                        "focus-visible:bg-red-500",
                        "rounded-[5px]",
                        "py-[15px]",
                        "px-[20px]",
                        "flex",
                        "items-center",
                        "gap-x-[8px]",
                        "cursor-pointer",
                        "max-[767px]:mb-[5px]",
                        "!outline-none",
                        getTransitionTwClasses("colors", 200)
                    ),
                icon: (): string => "mr-[8px]",
                text: (): string => "font-[300] text-gray-200 text-[16px] leading-[24px]",
                textStrong: (): string => "font-[800] mr-[5px]"
            }
        }
    },
    elements: {
        title: (): string => "px-[10px]"
    }
} as const
